<script setup>
import {computed, defineProps, ref, onMounted} from 'vue'
import { useRouter } from 'vue-router'
import Text from "@/components/common/Text.vue";
import {favoritesStore} from "@/stores/favoritesStore.js";
import {useI18n} from "vue-i18n";

const router = useRouter()
const { t } = useI18n();

const props = defineProps({
    cards: {
        type: Array,
        required: true,
        default: () => [],
        validator: (value) =>
            value.every(
                (item) =>
                    "title" in item &&
                    "subtitle" in item &&
                    "icon" in item &&
                    "routeName" in item
            ),
    },
})

const goTo = (routeName) => {
    if (routeName) {
        router.push(routeName)
    }
}

const favStore = favoritesStore();

function toggleFavorite(card) {
    const route = card.routeName?.startsWith('/') ? card.routeName : '/' + card.routeName;

    favStore.toggleFavorite({
        title: card.title,
        route,
    });
}

function isSelected(card) {
    return favStore.favoriteCards.some(f => f.title === card.title);
}

const sortedCards = computed(() => {
    return [...props.cards].sort((a, b) => {
        const nameA = t(a.title).toLowerCase();
        const nameB = t(b.title).toLowerCase();
        return nameA.localeCompare(nameB, undefined,  { sensitivity: 'base' });
    });
});

onMounted(() => {});
</script>

<template>
    <div class="nav-card trezo-card border-radius d-block bg-white border-0 shadow-none">
        <div class="overview-card-content">
            <v-row>
                <v-col
                    v-for="(card, index) in sortedCards"
                    :key="index"
                    cols="12"
                    md="4"
                >
                    <v-card
                        class="box bg-primary-50 border-radius v-card--hover h-100"
                        hover
                        @click="goTo(card.routeName)"
                        style="cursor: pointer"
                    >
                        <v-icon
                            :class="isSelected(card) ? 'favorite-icon-yellow' : 'favorite-icon-white'"
                            class="position-absolute"
                            style="top: 10px; right: 10px; z-index: 10;"
                            size="26"
                            @click.stop="toggleFavorite(card)"

                        >
                            {{  "mdi-star" }}
                        </v-icon>

                        <div class="d-flex align-items-center">
                            <div class="icon text-dark lh-1">
                                <i class="material-symbols-outlined">{{ card.icon }}</i>
                            </div>
                            <div>
                                <Text tag="h5" color="dark">{{ t(card.title) }}</Text>
                            </div>
                        </div>
                        <div class="info d-flex align-items-center justify-space-between">
                            <Text tag="span" color="dark">{{ t(card.subtitle) }}</Text>
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </div>
    </div>
</template>

<style lang="scss" scoped>

.favorite-icon-white {
    color: #fff!important;
}
.favorite-icon-yellow {
    color: #ff9105 !important;
}
.favorite-icon {
    position: absolute;
    top: 10px;
    right: 12px;
    font-size: 26px;
    cursor: pointer;
    transition: .2s ease;
}

.favorite-icon:hover {
    transform: scale(1.2);
}

.nav-card {
    padding-bottom: 0;

    .overview-card-content {
        .box {
            padding: 22px 20px;
            border-radius: 7px;

            .icon {
                margin-right: 10px;

                i {
                    font-size: 40px;
                    font-weight: 600;
                }
            }
            h5 {
                font-size: 20px;
                margin-top: 3px;
            }
            .info {
                margin-top: 23px;

                .sub-title {
                    font-size: 13px;
                }
                .info-badge {
                    font-size: 13px;
                    color: #1e8308;
                    padding: 3px 8.3px;
                    border-radius: 100px;
                    border: 1px solid #82fc5a;
                    background-color: #d8ffc8;
                    line-height: 1.2;

                    &.down {
                        border-color: #ffcea9;
                        color: var(--dangerColor);
                        background-color: #ffe1dd;
                    }
                }
                .users {
                    .v-img {
                        width: 34px;
                        height: 34px;
                        margin-right: -14px;
                        border: 1px solid var(--whiteColor);
                    }
                    span {
                        width: 34px;
                        height: 34px;
                        font-size: 12px;
                        border: 1px solid var(--whiteColor);
                        z-index: 1;
                    }
                }
            }
        }
        .v-col-sm-6 {
            &:nth-child(4) {
                .box {
                    .info {
                        margin-top: 15px;
                    }
                }
            }
        }
    }
}

/* Max width 767px */
@media only screen and (max-width: 767px) {
    .nav-card {
        .overview-card-content {
            .box {
                padding: 17px;
                margin-bottom: 17px;

                .icon {
                    i {
                        font-size: 35px;
                    }
                }
                h5 {
                    font-size: 18px;
                    margin-top: 7px;
                }
                .info {
                    margin-top: 12px;
                }
            }
            .v-col-sm-6 {
                &:nth-child(4) {
                    .box {
                        .info {
                            margin-top: 12px;
                        }
                    }
                }
            }
        }
    }
}

/* Min width 576px to Max width 767px */
@media only screen and (min-width: 576px) and (max-width: 767px) {
    .nav-card {
        .overview-card-content {
            .row {
                margin: {
                    left: -8.5px;
                    right: -8.5px;
                }
                .v-col-sm-6 {
                    padding: {
                        left: 8.5px;
                        right: 8.5px;
                    }
                }
            }
            .box {
                padding: 20px;
                margin-bottom: 20px;

                .info {
                    margin-top: 20px;
                }
            }
            .v-col-sm-6 {
                &:nth-child(4) {
                    .box {
                        .info {
                            margin-top: 10px;
                        }
                    }
                }
            }
        }
    }
}

/* Min width 768px to Max width 991px */
@media only screen and (min-width: 768px) and (max-width: 991px) {
    .nav-card {
        .overview-card-content {
            .box {
                padding: 20px;

                h5 {
                    font-size: 19px;
                }
                .info {
                    margin-top: 20px;
                }
            }
            .v-col-sm-6 {
                &:nth-child(4) {
                    .box {
                        .info {
                            margin-top: 10px;
                        }
                    }
                }
            }
        }
    }
}
</style>
